import { api } from '@/lib/api';
import type {
  PresignedUpload,
  VesselDocument,
  VesselDocumentDownloadUrl,
  VesselDocumentKind,
  VesselPhotoUrl,
} from './types';

/**
 * Vessel storage REST client (L1.2 backend contract).
 *
 * Photo:
 *   POST   /vessels/:id/photo/presign-upload
 *   POST   /vessels/:id/photo/confirm-upload
 *   GET    /vessels/:id/photo/url
 *   DELETE /vessels/:id/photo
 *
 * Documents:
 *   POST   /vessels/:id/documents/presign-upload
 *   POST   /vessels/:id/documents
 *   GET    /vessels/:id/documents
 *   GET    /vessels/:id/documents/:docId/download-url
 *   DELETE /vessels/:id/documents/:docId
 */
export const vesselStorageApi = {
  // ---------- Photo ----------
  presignPhotoUpload: async (
    vesselId: string,
    input: { contentType: string; contentLength: number; fileName?: string },
  ): Promise<PresignedUpload> => {
    const { data } = await api.post<PresignedUpload>(
      `/vessels/${vesselId}/photo/presign-upload`,
      input,
    );
    return data;
  },

  confirmPhotoUpload: async (
    vesselId: string,
    input: { fileKey: string; contentType: string; sizeBytes: number },
  ): Promise<{ photoKey: string }> => {
    const { data } = await api.post<{ photoKey: string }>(
      `/vessels/${vesselId}/photo/confirm-upload`,
      input,
    );
    return data;
  },

  getPhotoUrl: async (vesselId: string): Promise<VesselPhotoUrl | null> => {
    const { data } = await api.get<VesselPhotoUrl | null>(
      `/vessels/${vesselId}/photo/url`,
    );
    return data;
  },

  deletePhoto: async (vesselId: string): Promise<void> => {
    await api.delete(`/vessels/${vesselId}/photo`);
  },

  // ---------- Documents ----------
  presignDocumentUpload: async (
    vesselId: string,
    input: { contentType: string; contentLength: number; fileName: string },
  ): Promise<PresignedUpload> => {
    const { data } = await api.post<PresignedUpload>(
      `/vessels/${vesselId}/documents/presign-upload`,
      input,
    );
    return data;
  },

  createDocument: async (
    vesselId: string,
    input: {
      kind: VesselDocumentKind;
      fileKey: string;
      fileName: string;
      contentType: string;
      sizeBytes: number;
    },
  ): Promise<VesselDocument> => {
    const { data } = await api.post<VesselDocument>(
      `/vessels/${vesselId}/documents`,
      input,
    );
    return data;
  },

  listDocuments: async (
    vesselId: string,
    filter?: { kind?: VesselDocumentKind },
  ): Promise<VesselDocument[]> => {
    const { data } = await api.get<VesselDocument[]>(
      `/vessels/${vesselId}/documents`,
      { params: filter?.kind ? { kind: filter.kind } : undefined },
    );
    return data;
  },

  getDocumentDownloadUrl: async (
    vesselId: string,
    docId: string,
  ): Promise<VesselDocumentDownloadUrl> => {
    const { data } = await api.get<VesselDocumentDownloadUrl>(
      `/vessels/${vesselId}/documents/${docId}/download-url`,
    );
    return data;
  },

  deleteDocument: async (vesselId: string, docId: string): Promise<void> => {
    await api.delete(`/vessels/${vesselId}/documents/${docId}`);
  },
};

/**
 * Direct PUT to MinIO via XHR — bypasses the axios `/api/*` client
 * because the presigned URL points at `/storage/...` (different
 * cookie scope, different path). XHR is the canonical way to get
 * progress events for an upload (`fetch()` does not expose them).
 *
 * Resolves with the response status; the SigV4 verifier on MinIO
 * returns 200 on success, 4xx on signature mismatch / size mismatch.
 */
export function uploadFileWithProgress(input: {
  url: string;
  file: Blob;
  contentType: string;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', input.url);
    xhr.setRequestHeader('Content-Type', input.contentType);

    if (input.signal) {
      input.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && input.onProgress) {
        input.onProgress(e.loaded, e.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(
            `Upload failed: HTTP ${xhr.status} ${xhr.statusText || '(no body)'}`,
          ),
        );
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () => reject(new Error('Upload aborted'));

    xhr.send(input.file);
  });
}
